// Turn a react-easy-crop selection into an upload-ready Blob.
//
// The cropped region is drawn onto a high-resolution canvas (up to 600x200, 3:1)
// and exported as PNG. Higher resolution keeps logos — especially ones with text
// — sharp wherever they're rendered small (Profile, PDF footer, copy/paste card),
// since downscaling a crisp source looks far better than upscaling a tiny one.
//
// PNG (not JPEG) is used on purpose: it's lossless, so text and edges stay crisp
// with no compression artifacts, and it preserves transparency (including the
// letterbox padding around off-aspect logos). The backend accepts PNG and JPEG,
// so PNG introduces no new format.
//
// Display size is controlled by each consumer's own CSS / PDF dimensions (and, for
// Outlook, explicit HTML width/height on the copy-card <img>) — NOT by the stored
// pixel size — so raising the resolution here does not enlarge the logo anywhere.

// Upper bound for the normalized logo. The crop is downscaled to fit within this
// box but never upscaled, so small uploads keep their own resolution.
export const LOGO_MAX_WIDTH = 600
export const LOGO_MAX_HEIGHT = 200

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', reject)
        image.src = src
    })

// pixelCrop is the { x, y, width, height } (in source pixels) from react-easy-crop.
// Always returns a PNG Blob.
export const getCroppedLogoBlob = async (imageSrc, pixelCrop) => {
    const image = await loadImage(imageSrc)

    // Downscale the crop to fit the bounds, never upscale.
    const scale = Math.min(
        1,
        LOGO_MAX_WIDTH / pixelCrop.width,
        LOGO_MAX_HEIGHT / pixelCrop.height,
    )
    const targetWidth = Math.max(1, Math.round(pixelCrop.width * scale))
    const targetHeight = Math.max(1, Math.round(pixelCrop.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const ctx = canvas.getContext('2d')

    // High-quality downscaling for sharp text/edges (guarded — the quality hint
    // isn't supported everywhere).
    ctx.imageSmoothingEnabled = true
    if ('imageSmoothingQuality' in ctx) {
        ctx.imageSmoothingQuality = 'high'
    }

    // The crop rectangle can extend beyond the image when the logo is scaled to
    // fit inside the aspect (letterboxed) — so draw only the region that overlaps
    // the image, into its correctly-scaled destination sub-rectangle. Uniform
    // scaleX/scaleY (target preserves the crop aspect) means the logo is never
    // distorted; the uncovered area stays transparent (PNG).
    const scaleX = targetWidth / pixelCrop.width
    const scaleY = targetHeight / pixelCrop.height
    const sx = Math.max(0, pixelCrop.x)
    const sy = Math.max(0, pixelCrop.y)
    const sRight = Math.min(image.width, pixelCrop.x + pixelCrop.width)
    const sBottom = Math.min(image.height, pixelCrop.y + pixelCrop.height)
    const sWidth = sRight - sx
    const sHeight = sBottom - sy

    if (sWidth > 0 && sHeight > 0) {
        ctx.drawImage(
            image,
            sx, sy, sWidth, sHeight,
            (sx - pixelCrop.x) * scaleX, (sy - pixelCrop.y) * scaleY, sWidth * scaleX, sHeight * scaleY,
        )
    }

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Could not process the image.'))),
            'image/png',
        )
    })
}
